import React from 'react';
import { Users, RefreshCw, Lock, Volume2, Clock, Shield } from 'lucide-react';
import { WhatsAppGroup } from '../../types/groupTypes';
import { isCacheValid, getEphemeralDurationText } from '../../services/groupsApi';
import { useTheme } from '../../contexts/ThemeContext';

// ============================================================================
// INTERFACES
// ============================================================================

interface WhatsAppInstance {
  name: string;
  displayName?: string;
  status: string;
}

interface GroupCardProps {
  group: WhatsAppGroup;
  onClick: () => void;
  onSync?: () => void;
  availableInstances?: WhatsAppInstance[];
}

// ============================================================================
// COMPONENT
// ============================================================================

export const GroupCard: React.FC<GroupCardProps> = ({ group, onClick, onSync, availableInstances = [] }) => {
  const cacheValid = isCacheValid(group.lastSyncedAt);
  const { theme } = useTheme();
  
  const handleSyncClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSync) {
      onSync();
    }
  };

  // Buscar displayName da instância
  const getInstanceDisplayName = () => {
    if (!availableInstances || availableInstances.length === 0) {
      return group.instanceName;
    }
    const instance = availableInstances.find(i => i.name === group.instanceName);
    return instance?.displayName || group.instanceName;
  };

  return (
    <div
      onClick={onClick}
      className="rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 cursor-pointer p-4 border hover:border-blue-400"
      style={{
        backgroundColor: 'var(--bg-card)',
        borderColor: 'var(--border-color)'
      }}
    >
      {/* Header com foto e nome */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3 flex-1">
          {/* Foto do grupo */}
          <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
            {group.pictureUrl ? (
              <img
                src={group.pictureUrl}
                alt={group.subject}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <span>{group.subject.charAt(0).toUpperCase()}</span>
            )}
          </div>

          {/* Nome e badges */}
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-normal truncate" style={{ color: 'var(--text-primary)' }}>
              {group.subject}
            </h3>
            
            {/* Badges de configuração */}
            <div className="flex items-center gap-1 mt-1 flex-wrap">
              {group.isUserAdmin && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-normal rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200">
                  <Shield className="w-3 h-3" />
                  Admin
                </span>
              )}
              
              {group.isAnnouncement && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-normal rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200">
                  <Volume2 className="w-3 h-3" />
                  Anúncio
                </span>
              )}
              
              {group.isLocked && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-normal rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200">
                  <Lock className="w-3 h-3" />
                  Travado
                </span>
              )}
              
              {group.ephemeralDuration > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-normal rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-200">
                  <Clock className="w-3 h-3" />
                  {getEphemeralDurationText(group.ephemeralDuration)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Botão de sincronizar */}
        {onSync && (
          <button
            onClick={handleSyncClick}
            className="p-2 rounded-lg transition-colors text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:text-blue-400 dark:hover:bg-gray-700"
            title="Sincronizar grupo"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Descrição (se houver) */}
      {group.description && (
        <p className="text-sm font-normal mb-3 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
          {group.description}
        </p>
      )}

      {/* Footer com informações */}
      <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: 'var(--border-color)' }}>
        <div className="flex items-center space-x-4 text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
          {/* Número de participantes */}
          <div className="flex items-center space-x-1">
            <Users className="w-4 h-4" />
            <span>{group.participantsCount}</span>
            <span className="hidden sm:inline">
              {group.participantsCount === 1 ? 'membro' : 'membros'}
            </span>
          </div>

          {/* Instância */}
          <div className="text-xs font-normal" style={{ color: 'var(--text-muted)' }}>
            {getInstanceDisplayName()}
          </div>
        </div>

        {/* Status do cache */}
        <div className="flex items-center">
          {cacheValid ? (
            <span className="inline-flex items-center gap-1 text-xs font-normal text-green-600 dark:text-green-400">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              Atualizado
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-normal text-orange-600 dark:text-orange-400">
              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
              Desatualizado
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupCard;

